import {
  useState,
  useRef,
  useEffect,
  useCallback,
  type ReactNode,
  type KeyboardEvent as ReactKeyboardEvent,
} from 'react';
import { createPortal } from 'react-dom';
import { Search, ChevronDown, Loader2 } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

// ─── useAnchoredPosition ──────────────────────────────────────────────────────
// Computes position: fixed coordinates for a portaled panel anchored below a trigger.
// Recalculates on scroll/resize while open; cleans up on close/unmount. The portaled
// dropdown avoids being clipped by overflow:auto ancestors (e.g. the line-items table).

interface AnchoredPos {
  top: number;
  left: number;
  width: number;
}

function useAnchoredPosition(
  triggerRef: React.RefObject<HTMLElement | null>,
  open: boolean,
): AnchoredPos {
  const [pos, setPos] = useState<AnchoredPos>({ top: 0, left: 0, width: 0 });

  const recalc = useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    setPos({ top: r.bottom, left: r.left, width: r.width });
  }, [triggerRef]);

  useEffect(() => {
    if (!open) return;
    recalc();
    window.addEventListener('scroll', recalc, { capture: true, passive: true });
    window.addEventListener('resize', recalc, { passive: true });
    return () => {
      window.removeEventListener('scroll', recalc, { capture: true });
      window.removeEventListener('resize', recalc);
    };
  }, [open, recalc]);

  return pos;
}

// ─── SearchCombobox ───────────────────────────────────────────────────────────
// A reusable typeahead that owns ONLY the dropdown + keyboard mechanics. The parent
// supplies the data (`results`), the row renderer (`renderResult`), and any "create
// new" UI (`footer`). A SINGLE always-editable input is used — there is no read-only
// "selected" display mode (that mode was the root of the clear-to-search bug).
//
// Behavior:
//  - Opens on focus when there's a query; shows results whenever
//    query.length >= 1 && results.length > 0, REGARDLESS of whether a selection exists.
//  - ArrowDown/ArrowUp move a visibly-highlighted index (bg-accent on the active row).
//  - Enter AND Tab (no shift, dropdown open with results) select the
//    highlighted-or-first result: preventDefault, onSelect(result), then onConfirmAdvance().
//    With a single result, Tab/Enter select it.
//  - Escape closes. Clearing the field to empty returns to an empty-but-ready search.
//  - The dropdown is portaled to document.body so it is never clipped.

export interface SearchComboboxProps<T> {
  /** Selected id — for input styling only (the input text is always `query`). */
  value: number | null;
  /** Controlled input text. */
  query: string;
  /** Called whenever the input text changes (typing / clearing). */
  onQueryChange: (q: string) => void;
  /** The result rows to render (parent owns fetching/filtering). */
  results: T[];
  /** Stable React key for a result row. */
  getKey: (r: T) => string | number;
  /** Renders the visible content of a result row; `active` = currently highlighted. */
  renderResult: (r: T, active: boolean) => ReactNode;
  /** Called when a result is chosen (click or keyboard). */
  onSelect: (r: T) => void;
  /**
   * When true the field accepts arbitrary text that isn't a result (e.g. free-text
   * line descriptions). When false, only results may be committed. Currently affects
   * only intent/semantics — committing free text is handled by the parent via
   * onQueryChange; kept as a prop so entity comboboxes can advertise select-only.
   */
  allowFreeText?: boolean;
  /**
   * The "create new" slot, rendered at the bottom of the dropdown (below results).
   * Not part of arrow-key navigation. Receives the current trimmed-able query.
   */
  footer?: (query: string) => ReactNode;
  /** Called immediately AFTER onSelect — used to advance focus (e.g. to Qty). */
  onConfirmAdvance?: () => void;
  /** Receives the <input> element on mount/unmount for a parent focus registry. */
  registerInput?: (el: HTMLInputElement | null) => void;
  placeholder?: string;
  disabled?: boolean;
  /** Shows a "Searching…" affordance in the dropdown. */
  loading?: boolean;
  /** Extra classes for the <input>. */
  className?: string;
  /** Optional message shown when query.length >= 1 but there are no results. */
  emptyMessage?: ReactNode;
  /** Optional message shown when the dropdown is open but the query is empty. */
  promptMessage?: ReactNode;
}

export function SearchCombobox<T>({
  value,
  query,
  onQueryChange,
  results,
  getKey,
  renderResult,
  onSelect,
  allowFreeText: _allowFreeText,
  footer,
  onConfirmAdvance,
  registerInput,
  placeholder,
  disabled,
  loading,
  className,
  emptyMessage,
  promptMessage,
}: SearchComboboxProps<T>) {
  const [open, setOpen] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const pos = useAnchoredPosition(containerRef as React.RefObject<HTMLElement | null>, open);

  const hasResults = results.length > 0;
  // Render results whenever there's a query and matches — independent of selection.
  const showResults = query.length >= 1 && hasResults;
  const footerNode = footer?.(query);
  // The status lines the panel can render (prompt while empty, empty-state while
  // searching) — null when their message prop wasn't supplied.
  const showPrompt = query.length < 1 && promptMessage != null;
  const showEmpty = query.length >= 1 && !hasResults && emptyMessage != null;
  // Only mount the panel when open AND it actually has something to show. This keeps
  // an empty bordered box from flashing on focus when an item is already selected.
  // A bare selected value never forces it closed — that is what fixes clear-to-search.
  const panelHasContent = loading || showResults || footerNode != null || showPrompt || showEmpty;
  const showPanel = open && panelHasContent;

  // Reset highlight to the first result whenever the results list changes. Keyed on
  // the results array (a new fetch returns a new ref) so it fires when results LOAD —
  // covers re-queries returning a same-count list of different items.
  useEffect(() => {
    setHighlightIndex(0);
  }, [results]);

  // Register the input in the parent's focus registry (for the cross-row focus chain).
  useEffect(() => {
    if (!registerInput) return;
    registerInput(inputRef.current);
    return () => registerInput(null);
  }, [registerInput]);

  // Close on outside click (check both the trigger container and the portaled menu).
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      const target = e.target as Node;
      // A node React removed from the DOM mid-click (e.g. a "create" row swapping
      // itself for an inline form) can never be "inside" the menu, so treating it as
      // an outside click and closing is always wrong — ignore it.
      if (!document.contains(target)) return;
      const inContainer = containerRef.current?.contains(target) ?? false;
      const inMenu = menuRef.current?.contains(target) ?? false;
      if (!inContainer && !inMenu) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  function commitSelect(r: T) {
    onSelect(r);
    setOpen(false);
    onConfirmAdvance?.();
  }

  function handleKeyDown(e: ReactKeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Escape') {
      if (open) {
        e.preventDefault();
        setOpen(false);
      }
      return;
    }
    if (!open) {
      // Re-open on the first navigation key after a manual Escape.
      if (e.key === 'ArrowDown' && hasResults) {
        e.preventDefault();
        setOpen(true);
      }
      return;
    }
    if (e.key === 'ArrowDown') {
      if (!showResults) return;
      e.preventDefault();
      setHighlightIndex((i) => Math.min(i + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      if (!showResults) return;
      e.preventDefault();
      setHighlightIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      if (!showResults) return;
      e.preventDefault();
      const idx = highlightIndex >= 0 && highlightIndex < results.length ? highlightIndex : 0;
      commitSelect(results[idx]);
    } else if (e.key === 'Tab' && !e.shiftKey) {
      // Tab selects the highlighted/first result, then lets the focus chain advance.
      // With no results, do nothing here — let the browser's default Tab proceed.
      if (!showResults) return;
      e.preventDefault();
      const idx = highlightIndex >= 0 && highlightIndex < results.length ? highlightIndex : 0;
      commitSelect(results[idx]);
    }
  }

  const panel = showPanel ? (
    <div
      ref={menuRef}
      style={{
        position: 'fixed',
        top: pos.top + 4,
        left: pos.left,
        minWidth: Math.max(pos.width, 240),
        zIndex: 9999,
      }}
      className="rounded-md border bg-background shadow-lg max-h-64 overflow-auto"
    >
      {loading && (
        <div className="flex items-center gap-2 px-3 py-2 text-xs text-muted-foreground">
          <Loader2 className="h-3 w-3 animate-spin" />
          Searching…
        </div>
      )}
      {!loading && showPrompt && (
        <p className="px-3 py-2 text-xs text-muted-foreground">{promptMessage}</p>
      )}
      {!loading && showEmpty && (
        <p className="px-3 py-2 text-xs text-muted-foreground">{emptyMessage}</p>
      )}
      {showResults &&
        results.map((r, i) => {
          const active = i === highlightIndex;
          return (
            <button
              key={getKey(r)}
              type="button"
              className={cn(
                'w-full flex items-center gap-2 px-3 py-2 text-xs text-left transition-colors',
                active ? 'bg-accent text-accent-foreground' : 'hover:bg-muted',
              )}
              onMouseDown={(e) => {
                // preventDefault keeps focus on the input so the focus chain (and any
                // blur-close) behaves predictably; we drive selection explicitly.
                e.preventDefault();
                commitSelect(r);
              }}
              onMouseEnter={() => setHighlightIndex(i)}
            >
              {renderResult(r, active)}
            </button>
          );
        })}
      {footerNode}
    </div>
  ) : null;

  return (
    <div ref={containerRef} className="relative">
      <div className="relative">
        <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 text-muted-foreground pointer-events-none" />
        <Input
          ref={inputRef}
          value={query}
          onChange={(e) => {
            onQueryChange(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          className={cn('pl-6 pr-6', value ? 'font-medium' : undefined, className)}
          disabled={disabled}
          autoComplete="off"
        />
        <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 h-3 w-3 text-muted-foreground pointer-events-none" />
      </div>
      {panel !== null && createPortal(panel, document.body)}
    </div>
  );
}
